import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [driveLink, setDriveLink] = useState("");
  const isRemedy = params.get("type") === "remedy";

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refreshUser?.();
          if (data.kind === "remedy" && data.product_id) {
            try {
              const acc = await api.get(`/remedies/access/${data.product_id}`);
              setDriveLink(acc.data.drive_link || "");
            } catch { /* link added later */ }
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch { /* keep trying */ }
      if (tries++ < 8) setTimeout(poll, 1800);
      else setStatus("pending");
    };
    poll();
  }, [params, refreshUser]);

  return (
    <div className="min-h-screen bg-background grid place-items-center px-5">
      <div className="text-center max-w-md" data-testid="payment-success">
        {status === "checking" && <><Loader2 size={44} className="animate-spin text-primary mx-auto mb-5" strokeWidth={1.5} /><p className="text-muted-foreground">Confirming your payment…</p></>}
        {status === "paid" && isRemedy && (<>
          <CheckCircle2 size={52} className="text-primary mx-auto mb-5" strokeWidth={1.5} />
          <h1 className="font-serif text-3xl mb-3">Payment successful!</h1>
          <p className="text-muted-foreground mb-8">Your digital content is ready. Tap below to open your download link{driveLink ? "" : " — if it doesn't appear yet, find it any time under Remedies → Access content"}.</p>
          {driveLink
            ? <a href={driveLink} target="_blank" rel="noreferrer"><Button className="rounded-full bg-primary text-primary-foreground font-semibold" data-testid="success-download-btn">Open your content</Button></a>
            : <Link to="/remedies"><Button variant="outline" className="rounded-full border-border">Go to Remedies</Button></Link>}
        </>)}
        {status === "paid" && !isRemedy && (<>
          <CheckCircle2 size={52} className="text-primary mx-auto mb-5" strokeWidth={1.5} />
          <h1 className="font-serif text-3xl mb-3">You're a member!</h1>
          <p className="text-muted-foreground mb-8">Your membership is active. All reports, prescriptions and the meditation library are now unlocked.</p>
          <Link to="/app"><Button className="rounded-full bg-primary text-primary-foreground font-semibold" data-testid="success-dashboard-btn">Go to your dashboard</Button></Link>
        </>)}
        {status === "pending" && (<>
          <Loader2 size={44} className="text-primary mx-auto mb-5" strokeWidth={1.5} />
          <h1 className="font-serif text-2xl mb-3">Payment processing</h1>
          <p className="text-muted-foreground mb-8">This is taking a moment. Your account will update once it's confirmed.</p>
          <Link to={isRemedy ? "/remedies" : "/app"}><Button variant="outline" className="rounded-full border-border">Continue</Button></Link>
        </>)}
        {status === "error" && (<>
          <XCircle size={52} className="text-destructive mx-auto mb-5" strokeWidth={1.5} />
          <h1 className="font-serif text-2xl mb-3">Payment not completed</h1>
          <p className="text-muted-foreground mb-8">We couldn't confirm this payment. You can try again.</p>
          <Link to={isRemedy ? "/remedies" : "/products/health-memberships"}><Button variant="outline" className="rounded-full border-border">{isRemedy ? "Back to Remedies" : "Back to memberships"}</Button></Link>
        </>)}
      </div>
    </div>
  );
}
